'use client';

import React from 'react';
import { Button, Card } from '@ui';
import { IconCloseX } from '@icons';

export type LPResult = {
  id: number | string;
  title: string;
  tags: string[];
};

export type MatchMode = 'and' | 'or';

type LogicPillsProps = {
  id?: string;
  className?: string;
  data?: LPResult[];
  suggestions?: string[];
  // controlled props
  selected?: string[];
  mode?: MatchMode;
  onSelectedChange?: (next: string[]) => void;
  onModeChange?: (mode: MatchMode) => void;
  onResultsChange?: (results: LPResult[]) => void;
  renderResult?: (r: LPResult) => React.ReactNode;
};

const DEFAULT_SUGGESTIONS = ['Version: II', 'Status: Deployed', 'Phase: UI Design'];

const LogicPills: React.FC<LogicPillsProps> = ({
  id = 'logicPillsCard',
  className = 'logic-pills',
  data,
  suggestions = DEFAULT_SUGGESTIONS,
  selected: controlledSelected,
  mode: controlledMode,
  onSelectedChange,
  onModeChange,
  onResultsChange,
  renderResult,
}) => {
  const ALL = React.useMemo(() => data ?? [], [data]);
  const [internalSelected, setInternalSelected] = React.useState<string[]>([]);
  const [internalMode, setInternalMode] = React.useState<MatchMode>('and');

  const selected = controlledSelected ?? internalSelected;
  const mode = controlledMode ?? internalMode;

  const results = React.useMemo(() => {
    if (!selected || selected.length === 0) return ALL;
    if (mode === 'and') return ALL.filter((r) => selected.every((s) => r.tags.includes(s)));
    return ALL.filter((r) => selected.some((s) => r.tags.includes(s)));
  }, [ALL, selected, mode]);

  React.useEffect(() => {
    onResultsChange?.(results);
  }, [results, onResultsChange]);

  const toggleChip = (value: string) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    if (controlledSelected) onSelectedChange?.(next);
    else setInternalSelected(next);
  };

  const clearAll = () => {
    if (controlledSelected) onSelectedChange?.([]);
    else setInternalSelected([]);
  };

  const setMode = (next: MatchMode) => {
    if (controlledMode) onModeChange?.(next);
    else setInternalMode(next);
  };

  return (
    <Card className={className} id={id}>
      <div className='row' style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <div className='logic-pill-row' id='lpRow' role='list'>
          {selected.length === 0 ? (
            // When the list is empty we still need a listitem child for ARIA
            <div role='listitem'>
              <small className='hint'>No filters selected</small>
            </div>
          ) : (
            selected.map((s, i) => (
              <div key={s} role='listitem' style={{ display: 'contents' }}>
                <Button
                  className='lp-pill chip btn-sm'
                  aria-label={`Remove ${s}`}
                  onClick={() => toggleChip(s)}
                  label={s}
                  icon={<IconCloseX />}
                  iconPosition='right'
                />
                {i < selected.length - 1 && (
                  <span
                    className='lp-connector'
                    aria-hidden='true'
                    data-connector={mode === 'and' ? 'AND' : 'OR'}
                  >
                    {mode === 'and' ? 'AND' : 'OR'}
                  </span>
                )}
              </div>
            ))
          )}
        </div>

        <div className='row' style={{ gap: 8 }}>
          <div className='btn-group' role='group' aria-label='Match mode'>
            <Button
              as='button'
              type='button'
              className='btn-ghost btn-sm'
              id='lpAnd'
              aria-pressed={mode === 'and'}
              onClick={() => setMode('and')}
              label='Match all'
            />
            <Button
              as='button'
              type='button'
              className='btn-ghost btn-sm'
              id='lpOr'
              aria-pressed={mode === 'or'}
              onClick={() => setMode('or')}
              label='Match any'
            />
          </div>
          <Button
            as='button'
            type='button'
            className='btn-ghost btn-sm'
            id='lpClear'
            onClick={clearAll}
            label='Clear all'
          />
        </div>
      </div>

      <div className='pills-container'>
        <small className='hint' id='lpSummary'>
          {results.length} results
        </small>
        <div className='logic-grid' id='lpResults'>
          {results.map((r) => (
            <div key={r.id} className='card metric'>
              {renderResult ? (
                renderResult(r)
              ) : (
                <>
                  <h3 className='metric-title'>{r.title}</h3>
                  <div className='metric-meta'>{r.tags.join(' • ')}</div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className='mt-16 sep' />
      <div className='row' id='lpSuggestions' aria-label='Add filters (demo)'>
        <span className='hint'>Add:</span>
        {suggestions.map((s) => (
          <Button
            key={s}
            as='button'
            type='button'
            className='btn-ghost btn-sm'
            data-add={s}
            onClick={() => toggleChip(s)}
            label={s}
          />
        ))}
      </div>
    </Card>
  );
};

export default LogicPills;
